import React, { useCallback, useEffect, useRef, useState } from "react";
import { Form, AutoComplete } from "antd";
import { AddressOption, JPInputAutoCompleteProps } from "./JPInputAutoComplete.model";
import { getAddress, getAddresses } from "@companion-core/shared/app/Utils/apiClient";
import { JPInputError } from "../JPInputError";
import InputLabel from "@companion-core/web/src/Components/InputLabel";
import debounce from "lodash/debounce";
import { GooglePlaceAddress } from "@companion-core/shared/app/Interfaces/adresses";
import "@companion-core/web/src/Assets/Styles/scss/inputautocomplete.scss";
import { useTranslation } from "react-i18next";
import { FontIcon } from "@companion-core/shared/app/Components/FontIcon";
import InputClearButton from "@companion-core/web/src/Components/InputClearButton";

export const JPInputAutoComplete: React.FC<JPInputAutoCompleteProps> = ({
  name,
  required = false,
  addressType,
  label,
  value,
  icon,
  inputIcon,
  placeholder,
  testID,
  disabled = false,
  payloadPath = name,
  selectWithoutFillingInput = false,
  isStateless = false,
  onOptionSelect,
  setFieldErrors,
  onValueChange,
  registerValidation,
}: JPInputAutoCompleteProps) => {
  const form = Form.useFormInstance(); // Get current context form instance
  const [inputValue, setInputValue] = useState<GooglePlaceAddress | string>(value ?? "");
  // Create a ref to store the latest value and avoid stale closure
  const inputValueRef = useRef(inputValue);
  inputValueRef.current = inputValue;

  const [typedText, setTypedText] = useState<string | null>(null);
  const [error, setError] = useState<string>("");
  const [options, setOptions] = useState<AddressOption[]>([]);
  const [activeOptionLabel, setActiveOptionLabel] = useState<string>("");
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [selectInstanceKey, setSelectInstanceKey] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const { t, i18n } = useTranslation();
  const language = i18n.language;

  useEffect(() => {
    if (!dropdownOpen || !containerRef.current) return;
    const input = containerRef.current.querySelector<HTMLInputElement>('[role="combobox"]');
    if (!input) return;
    const updateActiveLabel = () => {
      const activeId = input.getAttribute("aria-activedescendant");
      if (!activeId) return;
      const activeEl = document.getElementById(activeId);
      setActiveOptionLabel(activeEl?.textContent?.trim() ?? "");
    };
    const observer = new MutationObserver(updateActiveLabel);
    observer.observe(input, {
      attributes: true,
      attributeFilter: ["aria-activedescendant"],
    });
    const timer = setTimeout(updateActiveLabel, 100);
    return () => {
      observer.disconnect();
      clearTimeout(timer);
    };
  }, [dropdownOpen, options]);

  useEffect(() => {
    if (!containerRef.current) return;
    const input = containerRef.current.querySelector<HTMLInputElement>(
      ".JPInputAutoComplete input.ant-select-input"
    );
    if (!input) return;

    // remove all aria attributes that are not needed (added by ant design)
    input.removeAttribute("aria-haspopup");
    input.removeAttribute("aria-controls");
    input.removeAttribute("aria-activedescendant");
    input.removeAttribute("aria-labelledby");

    if (error) {
      // If there is an error, the error message will be displayed instead of the required message
      input.removeAttribute("aria-required");
    } else {
      input.setAttribute("aria-required", required.toString());
    }
  }, [error]);

  useEffect(() => {
    if (registerValidation) {
      registerValidation(name, validateInput);
    }
  }, []);

  const handleSearch = useCallback(
    async (valueSearched: string) => {
      const normalizedValue = valueSearched.trim();
      if (!normalizedValue) return;
      let addresses: AddressOption[] = [];
      const response = await getAddresses(valueSearched, addressType, language);
      if (response.success && response.data) {
        addresses = response.data.map(({ placePrediction }) => ({
          key: placePrediction.placeId,
          value: placePrediction.text.text,
          label: placePrediction.text.text,
          placeId: placePrediction.placeId,
          placeLevel: placePrediction.types[0],
        }));
      }
      setOptions(addresses);
    },
    [addressType, language]
  );

  const debouncedSearch = useCallback(
    debounce((nextValue: string) => handleSearch(nextValue), 300),
    [handleSearch]
  );

  const updateValue = async (selectedOption: AddressOption) => {
    const { response: address } = await getAddress(selectedOption.placeId, language, addressType);
    if (!address) return;
    if (form && payloadPath) {
      form.setFieldsValue({
        [payloadPath]: address,
      });
    }
    setInputValue(address);
    setTypedText(null);
    validateInput(address); // We need to re-validate input on change to delete error if any
    if (selectWithoutFillingInput) {
      onOptionSelect?.(address, { placeId: selectedOption.placeId, placeLevel: address.type });
      clearSelectState();
    }
    if (onValueChange) {
      onValueChange(address);
    }
    if (isStateless && !selectWithoutFillingInput) {
      clearSelectState();
    }
  };

  const validateInput = (newValue?: GooglePlaceAddress | string) => {
    const inputValueString = newValue ?? inputValueRef.current;
    const err = !inputValueString && required ? "common.form.input.error.required" : "";
    setError(err);
    if (setFieldErrors) {
      setFieldErrors(err ? [err] : []);
    }
  };

  const inputId = `${name}-autocomplete`;
  const errorId = `${name}-error`;
  const selectedText = typeof inputValue === "string" ? inputValue : inputValue.formatted_address;
  const displayValue = typedText ?? selectedText;

  const resetInput = () => {
    setInputValue("");
    setTypedText(null);
    setOptions([]);
    setActiveOptionLabel("");
    setDropdownOpen(false);
  };

  const clearSelectState = () => {
    resetInput();
    if (isStateless) {
      setSelectInstanceKey((prev) => prev + 1);
    }
  };

  const clearInput = () => {
    clearSelectState();
    if (form && payloadPath) {
      form.setFieldsValue({
        [payloadPath]: undefined,
      });
    }
    onValueChange?.(null);
    setTimeout(() => {
      const input = containerRef.current?.querySelector<HTMLInputElement>(`#${inputId}`);
      input?.focus();
    }, 0);
  };

  return (
    <div className="JPInputAutoComplete" ref={containerRef}>
      {label && (
        <InputLabel label={label} icon={icon} htmlFor={inputId} id={`${name}-autocomplete-label`} />
      )}
      <AutoComplete
        key={`${name}-select-${selectWithoutFillingInput ? "preserve" : "default"}-${selectInstanceKey}`}
        id={inputId}
        prefix={inputIcon ? <FontIcon name={inputIcon} size={16} /> : undefined}
        data-testid={testID}
        value={displayValue}
        placeholder={!disabled ? placeholder : ""}
        options={options}
        showSearch={{
          onSearch: debouncedSearch,
          filterOption: false,
        }}
        notFoundContent={null}
        suffix={
          displayValue ? (
            <InputClearButton
              ariaLabel={t("common.form.input.actions.clear")}
              testID={testID}
              isInputSelect={true}
              clearInput={clearInput}
            />
          ) : undefined
        }
        defaultActiveFirstOption={false}
        virtual={false}
        status={error ? "error" : undefined}
        disabled={disabled}
        onBlur={() => {
          setTypedText(null);
          validateInput();
        }}
        onChange={(text: string) => setTypedText(text ?? "")}
        onSelect={(_: string, option: AddressOption) => updateValue(option)}
        onOpenChange={(open) => {
          setDropdownOpen(!!open);
          if (!open) setActiveOptionLabel("");
        }}
        optionRender={(option, { index }) => (
          <div data-testid={`${testID}-option-${index}`}>{option.label}</div>
        )}
        aria-required={required}
      />
      <div
        role="status"
        aria-live="polite"
        aria-atomic="true"
        aria-hidden={!activeOptionLabel}
        className="JPInputAutoComplete__sr-only"
        data-testid={testID ? `${testID}-live-region` : undefined}>
        {activeOptionLabel}
      </div>
      {error && <JPInputError inputLabel={label} testID={testID} error={error} id={errorId} />}
    </div>
  );
};
